import clsx from "clsx";
import AddHardwareModal from "@app/components/add-hardware-modal";
import ExtendCloudModal from "@app/components/extend-cloud-modal";
import ConfirmModal from "@app/components/confirm-modal";
import PlayPcrenderModal from "@app/components/play-pcrender-modal";
import { addToast } from "@app/components/toast/toast.service";
import { TunnelModal } from "@app/components/wan-modal";
import QRCodeModal from "@app/components/wan-modal/qr-modal.component";
import {
  CURRENT_ACCOUNT,
  DEFAULT_PAGE,
  DEFAULT_TOTAL_PAGES,
  PathnameUrl,
} from "@app/constants";
import AccountService from "@app/services/http/account.service";
import CloudService from "@app/services/http/cloud.service";
import ImageCloudService from "@app/services/http/image.cloud.service";
import LogService from "@app/services/http/log.service";
import SshService from "@app/services/http/ssh.service";
import { openPortalDialog } from "@app/services/modal.service";
import SocketService from "@app/services/socket.service";
import { storeMyInfo } from "@app/store/my-info/my-info.action";
import { Account, Cloud, ImageCloud, LogRequest, Tunnel } from "@app/types";
import { bySortOrder, downloadFile, formatDate, getCloudExpiry } from "@app/utils/util";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import useBusy from "@core/hooks/use-busy.hook";
import HttpService from "@core/services/http/http.service";
import { useNow, useSilentSync } from "@core/hooks/use-silent-sync.hook";
import dayjs from "dayjs";
import StorageService from "@core/services/storage";
import { useEffect, useState } from "react";
import { Subscription } from "rxjs";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useT } from "@app/contexts/preferences.context";
import { IcCloud } from "@app/components/icons";
import { btn, btnGhost } from "@app/components/ui";
import CloudCatalog from "./cloud-catalog";
import CloudSummary from "./cloud-summary";
import CloudImages from "./cloud-images";

/** Trang Cloud Gaming - Render: logic lấy từ CloudPage, CloudCreatePage, CreateCloudModal và ImageCloudCard cũ. */
function CloudGamingContent() {
  const t = useT();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [imageCloud, setImageCloud] = useState<ImageCloud[]>([]);
  const [clouds, setClouds] = useState<Cloud[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<Account>();

  const [selectCloud, setSelectCloud] = useState<Cloud>();
  const [imageName, setImageName] = useState("");
  const [agree, setAgree] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);

  const [update, forceUpdate] = useForceUpdate();
  const { subscribeOnce, subscribeUntilDestroy } = useObservable();
  // thao tác trên từng Image: chỉ nút đó xoay, không che cả trang (key "<thao tác>:<id Image>")
  const { busy: rowBusy, run } = useBusy();

  const getMyInfo = () => {
    subscribeOnce(AccountService.getMyInfo(), (account) => {
      if (account) {
        setCurrentAccount(account);
        StorageService.setObject(CURRENT_ACCOUNT, account);
        dispatch(storeMyInfo(account));
      }
    });
  };

  /*
   * Trạng thái Còn máy / Hết máy (cloudReady): như trang Thuê máy tính, hỏi GET /clouds/availability mỗi 5s
   * (server giữ kết quả 10s dùng chung cho mọi người). Chỉ thay object của gói có đổi trạng thái,
   * không đổi gì thì giữ nguyên state nên không render lại, không nháy.
   */
  const applyReady = (list: Pick<Cloud, "_id" | "cloudReady">[]) => {
    const byId = new Map(list.map((item) => [String(item._id), item.cloudReady]));

    setClouds((prev) => {
      const next = prev.map((cloud) => {
        const ready = byId.get(cloud._id);

        return ready === undefined || ready === cloud.cloudReady ? cloud : { ...cloud, cloudReady: ready };
      });

      return next.some((cloud, i) => cloud !== prev[i]) ? next : prev;
    });
  };

  // Sau khi tạo Image / có kết quả máy: hỏi trực tiếp máy chủ Cloud (GET /clouds), không đợi cache
  const refreshClouds = () =>
    CloudService.getClouds(true).subscribe({ next: (next) => applyReady(next || []), error: () => undefined });

  useEffect(() => {
    getMyInfo();
    // gói theo thứ tự admin nhập (API đã xếp, xếp lại cho chắc); các lần hỏi sau chỉ thay cloudReady nên thứ tự giữ nguyên
    subscribeOnce(CloudService.getClouds(), (list) => setClouds([...(list || [])].sort(bySortOrder)));
  }, []);

  // Gói đang chọn vừa hết máy thì bỏ chọn như thẻ Hết máy (không bấm chọn được)
  useEffect(() => {
    if (!selectCloud) return;

    const fresh = clouds.find((c) => c._id === selectCloud._id);

    if (fresh && !fresh.cloudReady) {
      setSelectCloud(undefined);
      addToast({ text: `${selectCloud.name}: ${t("gói vừa hết máy, vui lòng chọn gói khác")}`, status: "warn" });
    }
  }, [clouds]);

  // Hỏi lại mỗi 5s khi đang xem trang: gọi nối tiếp (không chồng request), tab ẩn thì dừng,
  // mở lại tab thì hỏi ngay, lỗi (mất mạng, 429…) thì thưa ra 15s
  useEffect(() => {
    const INTERVAL = 5000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sub: Subscription | undefined;
    let inFlight = false;
    let stopped = false;

    const schedule = (ms = INTERVAL) => {
      if (!stopped) timer = setTimeout(tick, ms);
    };

    function tick() {
      if (stopped || inFlight) return;

      if (document.hidden) {
        schedule();

        return;
      }

      inFlight = true;
      sub = CloudService.getAvailability().subscribe({
        next: (list) => {
          inFlight = false;
          applyReady(list || []);
          schedule();
        },
        error: () => {
          inFlight = false;
          schedule(15000);
        },
      });
    }

    const onVisible = () => {
      if (!document.hidden && !inFlight) {
        clearTimeout(timer);
        tick();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      clearTimeout(timer);
      sub?.unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    const subCreateVm = SocketService.onCreateVmResult().subscribe((data) => {
      forceUpdate();
      refreshClouds();
      addToast({ text: data?.message });
    });
    const subVm = SocketService.onVmResult().subscribe((data) => {
      forceUpdate();
      refreshClouds();
      addToast({ text: data?.message });
    });

    return () => {
      subCreateVm.unsubscribe();
      subVm.unsubscribe();
    };
  }, []);

  // Hạn sử dụng (ngày tạo + days_of_use ngày) và giờ tự tắt của session: tới mốc là giao diện đổi ngay
  const now = useNow(
    imageCloud
      .filter((image) => !image.has_expired && !image.is_deleted)
      .flatMap((image) => [
        getCloudExpiry(image).valueOf(),
        image.is_running && image.cloud?.max_hours_each_session
          ? dayjs(image.last_turned_time).add(image.cloud.max_hours_each_session, "hour").valueOf()
          : 0,
      ])
  );

  // Image đang chờ máy: vị trí và thời gian chờ đổi theo người khác (cron hàng chờ của server chạy mỗi 30s)
  const hasQueued = imageCloud.some((image) => (image.queue_at || 0) > 0 && !image.has_expired && !image.is_deleted);

  // Đồng bộ nền mỗi 60s (cron Cloud của server chạy mỗi phút: cộng giờ chơi, tự tắt khi hết session / hết giờ, đánh dấu hết hạn)
  // khi có máy đang chạy / đang tạo / vừa tới hạn; có Image đang chờ máy thì 20s. Im lặng, chỉ thay khi dữ liệu đổi.
  useSilentSync(
    () =>
      ImageCloudService.getImageCloud("", page, {}, true).subscribe((res) => {
        const next = res?.data || [];

        if (JSON.stringify(next) !== JSON.stringify(imageCloud)) {
          setImageCloud(next);
          setTotalPages(next.length ? res.total_page : 0);
        }
      }),
    hasQueued ||
      imageCloud.some(
        (image) =>
          image.is_running ||
          image.is_creating ||
          (!image.has_expired && getCloudExpiry(image).valueOf() <= now)
      ),
    hasQueued ? 20000 : 60000
  );

  useEffect(() => {
    subscribeUntilDestroy(ImageCloudService.getImageCloud("", page), (res) => {
      setImagesLoaded(true);

      if (res?.data?.length) {
        setImageCloud(res.data);
        setTotalPages(res.total_page);
      } else {
        setImageCloud([]);
        setTotalPages(0);
      }
    });
  }, [update, page]);

  /* ---------- tạo Cloud (CreateCloudModal cũ) ---------- */
  const handlePick = (cloud?: Cloud) => {
    setSelectCloud(cloud);
    setShowErrors(false);
  };

  const handleCreate = () => {
    if (!selectCloud) return;

    // Theo thiết kế: bắt buộc nhập tên Image và đồng ý điều khoản trước khi tạo
    if (!imageName.trim() || !agree) {
      setShowErrors(true);

      return;
    }

    setBusy(true);

    // nút Tạo Image hiện "Đang tạo…", không che cả trang
    HttpService.local(() => ImageCloudService.createImageCloud({ name: imageName.trim(), cloud: selectCloud._id })).subscribe({
      next: () => {
        setBusy(false);
        handlePick(undefined);
        setImageName("");
        setAgree(false);
        getMyInfo();
        forceUpdate();
        refreshClouds();
        document.getElementById("cloud-images")?.scrollIntoView({ behavior: "smooth", block: "start" });
      },
      // lỗi (vd "Cloud Full") thì hỏi lại trạng thái các gói
      error: () => {
        setBusy(false);
        refreshClouds();
      },
    });
  };

  /* ---------- thao tác (CloudPage cũ) ---------- */
  const confirmThen = (
    confirm: { title: string; message: string; confirmLabel: string; danger?: boolean },
    action: () => void
  ) => {
    openPortalDialog(ConfirmModal, confirm)
      .afterClosed()
      .subscribe((data) => data?.isAccept && action());
  };

  /** Tên hiện trong hộp xác nhận / thông báo */
  const nameOf = (imageId: string) => {
    const image = imageCloud.find((item) => item._id === imageId);

    return image?.name || image?.cloud?.name || "Cloud";
  };

  const onConnectImageClick = (imageId: string) => {
    run(`rdp:${imageId}`, () => ImageCloudService.getCloudRdpFile(imageId), (data) => {
      const contentDisposition = data.responseHeaders["content-disposition"];
      const fileNameOriginal = contentDisposition?.split("filename=")?.[1];

      if (fileNameOriginal) {
        const fileName = fileNameOriginal.substring(1, fileNameOriginal.length - 2);

        if (fileName) {
          downloadFile(data.response, "application/rdp", fileName);
        }
      }
    });
  };

  const handleStartClick = (imageId: string) =>
    confirmThen(
      {
        title: `${t("Bật máy")} ${nameOf(imageId)}?`,
        message: t("Máy bắt đầu tính giờ chơi ngay khi bật."),
        confirmLabel: t("Bật máy"),
      },
      () =>
        run(`start:${imageId}`, () => ImageCloudService.startImage(imageId), (data) => {
          // Gói Check GPU mà GPU đang có người dùng / có người chờ: API xếp vào hàng chờ, tới lượt tự bật
          if (data?.queued) {
            addToast({ text: data.message, status: "warn" });
          } else if (data?.statusCode === 400) {
            // bật thất bại (nhánh Check GPU): câu do API viết, báo bằng toast
            addToast({ text: data.message, status: "inValid" });
          }

          forceUpdate();
        })
    );

  // Rời hàng chờ: theo thiết kế bấm là rời luôn, không hỏi lại
  const handleLeaveQueue = (image: ImageCloud) =>
    run(`leave:${image._id}`, () => ImageCloudService.leaveQueue(image._id), () => {
      addToast({ text: `${image.name || image.cloud?.name} ${t("đã rời hàng chờ")}` });
      forceUpdate();
    });

  const handleReStartClick = (imageId: string) =>
    confirmThen(
      {
        title: `${t("Khởi động lại")} ${nameOf(imageId)}?`,
        message: t("Máy sẽ tắt rồi bật lại, giờ chơi vẫn được tính trong lúc khởi động lại."),
        confirmLabel: t("Khởi động lại"),
      },
      () => run(`restart:${imageId}`, () => ImageCloudService.reStartImage(imageId), () => forceUpdate())
    );

  // Xoá Cloud: câu theo bản thiết kế
  const handleDeleteImageVM = (imageId: string) => {
    const name = nameOf(imageId);
    const running = imageCloud.find((item) => item._id === imageId)?.is_running;

    confirmThen(
      {
        title: `${t("Xoá Cloud")} ${name}?`,
        message: `${running ? `${t("Máy đang chạy sẽ bị tắt.")} ` : ""}${t(
          "Dữ liệu trong Cloud và bản sao lưu sẽ mất, không thể khôi phục. Giờ chơi còn lại không được hoàn tiền."
        )}`,
        confirmLabel: t("Xoá Cloud"),
        danger: true,
      },
      () =>
        run(`delete:${imageId}`, () => ImageCloudService.deleteImageVM(imageId), (data) => {
          if (data.status) {
            addToast({ text: `${t("Đã xoá Cloud")} ${name}` });
            forceUpdate();
          } else {
            addToast({ text: `${t("Xoá Cloud")} ${name} ${t("thất bại")}`, status: "inValid" });
          }
        })
    );
  };

  // Sao lưu / Khôi phục: câu theo bản thiết kế
  const handleBackupImageVM = (imageId: string) => {
    const backupAt = imageCloud.find((item) => item._id === imageId)?.backup_response_data?.created_at;

    confirmThen(
      {
        title: `${t("Sao lưu")} ${nameOf(imageId)}?`,
        message: `${t("Tạo bản sao lưu toàn bộ máy ở thời điểm này.")}${
          backupAt ? ` ${t("Bản sao lưu cũ")} (${formatDate(backupAt)}) ${t("sẽ được thay thế.")}` : ""
        }`,
        confirmLabel: t("Sao lưu"),
      },
      () => run(`backup:${imageId}`, () => ImageCloudService.backupImageVM(imageId), () => forceUpdate())
    );
  };

  const handleRestoreImageVM = (imageId: string) => {
    const backupAt = imageCloud.find((item) => item._id === imageId)?.backup_response_data?.created_at;

    confirmThen(
      {
        title: `${t("Khôi phục")} ${nameOf(imageId)}?`,
        message: `${t("Máy sẽ quay về bản sao lưu")}${backupAt ? ` ${formatDate(backupAt)}` : ""}. ${t(
          "Mọi thay đổi sau thời điểm đó sẽ mất."
        )}`,
        confirmLabel: t("Khôi phục"),
      },
      () => run(`restore:${imageId}`, () => ImageCloudService.restoreImageVM(imageId), () => forceUpdate())
    );
  };

  const handleShutdownImageVM = (imageId: string) =>
    confirmThen(
      {
        title: `${t("Tắt máy")} ${nameOf(imageId)}?`,
        message: t("Máy ngừng tính giờ và lưu dữ liệu. Sau khi tắt cần chờ 2 phút mới bật lại được."),
        confirmLabel: t("Tắt máy"),
      },
      () => run(`shutdown:${imageId}`, () => ImageCloudService.shutdownImageVM(imageId), () => forceUpdate())
    );

  // Gia hạn: popup theo bản thiết kế (thay hộp xác nhận cũ), thiếu tiền thì chuyển sang Nạp tiền. API gia hạn giữ nguyên.
  const handleExtendClick = (image: ImageCloud) => {
    openPortalDialog(ExtendCloudModal, { image, currentAccount })
      .afterClosed()
      .subscribe((data) => {
        if (data?.topup) {
          navigate(`${PathnameUrl.RECHARGE}?amount=${Math.ceil(data.topup)}&from=extend_cloud`);

          return;
        }

        if (data?.times) {
          run(`extend:${image._id}`, () => ImageCloudService.extendsImageVM(image._id, data.times), () => {
            addToast({ text: `Đã gia hạn ${image.name}` });
            getMyInfo();
            forceUpdate();
          });
        }
      });
  };

  const handleAddHardware = (image: ImageCloud) => {
    openPortalDialog(AddHardwareModal, { imageCloud: image })
      .afterClosed()
      .subscribe((data) => data?.isOk && forceUpdate());
  };

  const onPlayPcrender = (image: ImageCloud) => {
    openPortalDialog(PlayPcrenderModal, { imageCloud: image })
      .afterClosed()
      .subscribe((data) => data?.token && forceUpdate());
  };

  const handleChoosePcrenderTunnel = (image: ImageCloud) => {
    if (!image.resource_cloud?.ssh?.api_tunnel) {
      return;
    }

    openPortalDialog(TunnelModal, image)
      .afterClosed()
      .subscribe((data) => {
        if (!data?.os) return;

        const tunnelRequest: Tunnel = {
          name: image.account.username,
          allowIp: image.resource_cloud.ip_int + "/32",
          os: data.os,
          image_id: image._id,
          api_tunnel: image.resource_cloud.ssh.api_tunnel,
        };

        run(`tunnel:${image._id}`, () => SshService.chooseTunnelForCloud(tunnelRequest), (data1) => {
          if (data.os === "mobile") {
            openPortalDialog(QRCodeModal, data1);

            return;
          }

          const contentDisposition = data1.responseHeaders["content-disposition"];
          const fileNameOriginal = contentDisposition?.split("filename=")?.[1];

          if (fileNameOriginal) {
            const fileName = fileNameOriginal.substring(1, fileNameOriginal.length - 2);

            if (fileName) {
              downloadFile(data1.response, "application/exe", fileName);
            }

            const logRequest: LogRequest = {
              message: fileName
                ? `Pcrender Tunnel - Tải thành công file ${fileName}`
                : `Pcrender Tunnel - Tải file exe KHÔNG thành công ${fileName}`,
            };

            subscribeOnce(LogService.createLogByUser(logRequest), () => {});
          }
        });
      });
  };

  const discount = currentAccount?.discount_cloud || 0;

  return (
    <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4 max-[760px]:gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">Cloud Gaming - Render</h1>
          <p className="m-0 mt-1 max-w-[62ch] text-muted">
            {t("Mua gói giờ chơi trên máy chủ có GPU. Mọi thứ bạn cài đặt được lưu trong Image Cloud riêng, lần sau mở lại vẫn còn nguyên.")}
          </p>
        </div>
        <button
          type="button"
          className={clsx(btn, btnGhost)}
          onClick={() => document.getElementById("cloud-images")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        >
          <IcCloud className="h-[18px] w-[18px]" />
          {t("Image của tôi")} ({imageCloud.length})
        </button>
      </div>

      <ol className="m-0 grid list-none grid-cols-3 gap-2.5 p-0 max-[760px]:grid-cols-1 max-[760px]:gap-1.5">
        {[
          ["Chọn gói", "So sánh giờ chơi, thời gian mỗi session và hàng chờ."],
          ["Đặt tên Image và thanh toán", "Image được tạo ngay, trừ tiền từ số dư."],
          ["Bấm Bật máy để bắt đầu tính giờ", "Tắt máy là ngừng tính giờ. Hết giờ chơi hoặc tới hạn sử dụng thì gia hạn."],
        ].map(([title, desc], i) => (
          <li
            key={title}
            className="flex items-start gap-2.5 rounded-card border border-line bg-surface px-3.5 py-2.5 text-[12.5px] leading-snug text-muted max-[760px]:items-center max-[760px]:px-3 max-[760px]:py-2"
          >
            <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-brand-soft text-xs font-bold text-brand">
              {i + 1}
            </span>
            <span>
              <b className="block text-[13px] font-semibold text-fg">{t(title)}</b>
              <span className="max-[760px]:hidden">{t(desc)}</span>
            </span>
          </li>
        ))}
      </ol>

      <div className="grid grid-cols-[minmax(0,1fr)_310px] items-stretch gap-5 max-[1240px]:grid-cols-[minmax(0,1fr)_300px] max-[1100px]:grid-cols-1">
        <CloudCatalog clouds={clouds} discount={discount} selectedId={selectCloud?._id || ""} onPick={handlePick} />
        <CloudSummary
          cloud={selectCloud}
          discount={discount}
          balance={currentAccount?.balance || 0}
          name={imageName}
          agree={agree}
          showErrors={showErrors}
          busy={busy}
          onName={setImageName}
          onAgree={setAgree}
          onUnpick={() => handlePick(undefined)}
          onCreate={handleCreate}
          onTopup={(need) => navigate(`${PathnameUrl.RECHARGE}?amount=${Math.ceil(need)}&from=cloud`)}
        />
      </div>

      <CloudImages
        now={now}
        loading={!imagesLoaded}
        images={imageCloud}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onConnect={onConnectImageClick}
        onStart={handleStartClick}
        onLeaveQueue={handleLeaveQueue}
        onReStart={handleReStartClick}
        onShutdown={handleShutdownImageVM}
        onBackup={handleBackupImageVM}
        onRestore={handleRestoreImageVM}
        onDelete={handleDeleteImageVM}
        onExtend={handleExtendClick}
        onAddHardware={handleAddHardware}
        onPlay={onPlayPcrender}
        onTunnel={handleChoosePcrenderTunnel}
        busy={rowBusy}
      />
    </div>
  );
}

export default CloudGamingContent;
